/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { OperationStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OperationSummaryProps {
  id: string;
  status: OperationStatus;
  unitId: string | null;
  vehicleId: string | null;
  riskAnalysisNumber: string | null;
  riskAnalysisAcknowledged: boolean | null;
  tripId: string | null;
}

export class OperationSummaryRecord extends Record<OperationSummaryProps>({
  id: null as any,
  status: null as any,
  unitId: null as any,
  vehicleId: null as any,
  riskAnalysisNumber: null as any,
  riskAnalysisAcknowledged: null as any,
  tripId: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: OperationStatus;
    unitId: string | null;
    vehicleId: string | null;
    riskAnalysisNumber: string | null;
    riskAnalysisAcknowledged: boolean | null;
    tripId: string | null;
  }): OperationSummaryRecord {
    if (json == null) {
      throw new Error('OperationSummaryRecord.fromJS: parameter cannot be null');
    }
    return new OperationSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OperationSummaryJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', OperationStatus.CrewAccepted),
        'OperationSummaryJson.status',
      ),
      unitId: json.unitId,
      vehicleId: json.vehicleId,
      riskAnalysisNumber: json.riskAnalysisNumber,
      riskAnalysisAcknowledged: json.riskAnalysisAcknowledged,
      tripId: json.tripId,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: OperationStatus;
    unitId: string | null;
    vehicleId: string | null;
    riskAnalysisNumber: string | null;
    riskAnalysisAcknowledged: boolean | null;
    tripId: string | null;
  }): [OperationSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('OperationSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OperationSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OperationSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', OperationStatus.CrewAccepted),
            'OperationSummaryJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () => json.unitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      riskAnalysisNumber: tryOrDefault(
        () => json.riskAnalysisNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      riskAnalysisAcknowledged: tryOrDefault(
        () => json.riskAnalysisAcknowledged,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripId: tryOrDefault(
        () => json.tripId,
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: OperationSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('OperationSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OperationSummaryViewModelBase implements OperationSummaryProps {
  protected readonly _props: OperationSummaryProps;
  private _json?: OperationSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get status(): OperationStatus {
    return this._props.status;
  }
  get unitId(): string | null {
    return this._props.unitId;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get riskAnalysisNumber(): string | null {
    return this._props.riskAnalysisNumber;
  }
  get riskAnalysisAcknowledged(): boolean | null {
    return this._props.riskAnalysisAcknowledged;
  }
  get tripId(): string | null {
    return this._props.tripId;
  }

  public get isAborted() {
    return this._props.status === OperationStatus.Aborted;
  }
  public get isCancelled() {
    return this._props.status === OperationStatus.Cancelled;
  }
  public get isCompleted() {
    return this._props.status === OperationStatus.Completed;
  }
  public get isCrewAccepted() {
    return this._props.status === OperationStatus.CrewAccepted;
  }
  public get isOnStandby() {
    return this._props.status === OperationStatus.OnStandby;
  }
  public get isScheduled() {
    return this._props.status === OperationStatus.Scheduled;
  }
  public get isUnderway() {
    return this._props.status === OperationStatus.Underway;
  }
  public get isUnitActivated() {
    return this._props.status === OperationStatus.UnitActivated;
  }

  public isAnyStatus(...operationStatus: OperationStatus[]) {
    return operationStatus.includes(this._props.status);
  }

  constructor(props: OperationSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        unitId: this._props.unitId,
        vehicleId: this._props.vehicleId,
        riskAnalysisNumber: this._props.riskAnalysisNumber,
        riskAnalysisAcknowledged: this._props.riskAnalysisAcknowledged,
        tripId: this._props.tripId,
      };
    }
    return this._json;
  }
}
